import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AnalyzeAudioParams, AnalyzeResponse, FetchNotesFilters, Note } from '@memorai/types';
import { analyzeAudio, appendToNote } from './analyze.js';
import {
    createCategory,
    deleteCategory,
    fetchCategories,
    updateCategory,
} from './categories.js';
import { deleteNote, fetchNote, fetchNotes, updateNote } from './notes.js';
import { fetchPreferences, updatePreferences } from './preferences.js';
import { getCurrentUser } from './users.js';
import { queryKeys } from './keys.js';

const PAGINATION_CONFIG = {
    DEFAULT_PAGE_SIZE: 5,
    INITIAL_LOAD_SIZE: 10,
} as const;

export function useAnalyze(activeProfileId?: string | null) {
    return useMutation<AnalyzeResponse, Error, Omit<AnalyzeAudioParams, 'profileId'>>({
        mutationFn: (params) => analyzeAudio({ ...params, profileId: activeProfileId }),
    });
}

export function useAppendToNote(activeProfileId?: string | null) {
    return useMutation<AnalyzeResponse, Error, Omit<AnalyzeAudioParams, 'profileId'> & { id: string }>({
        mutationFn: ({ id, ...params }) => appendToNote(id, { ...params, profileId: activeProfileId }),
    });
}

export function useCategories() {
    return useQuery({
        queryKey: queryKeys.categories,
        queryFn: fetchCategories,
    });
}

export function useCreateCategory() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.categories });
        },
    });
}

export function useUpdateCategory() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: { name?: string; color?: string } }) =>
            updateCategory(id, updates),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.categories });
        },
    });
}

export function useDeleteCategory() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteCategory(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.categories });
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });
}

export function useNotes(filters?: FetchNotesFilters) {
    return useQuery({
        queryKey: queryKeys.notes(filters),
        queryFn: () => fetchNotes(50, 0, filters),
    });
}

export function useInfiniteNotes(filters?: FetchNotesFilters) {
    return useInfiniteQuery({
        queryKey: queryKeys.infiniteNotes(filters),
        queryFn: ({ pageParam = 0 }) => {
            const limit = pageParam === 0
                ? PAGINATION_CONFIG.INITIAL_LOAD_SIZE
                : PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;

            const offset = pageParam === 0
                ? 0
                : PAGINATION_CONFIG.INITIAL_LOAD_SIZE + (pageParam - 1) * PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;

            return fetchNotes(limit, offset, filters);
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            const lastPageLimit = allPages.length === 1
                ? PAGINATION_CONFIG.INITIAL_LOAD_SIZE
                : PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;

            if (lastPage.length < lastPageLimit) {
                return undefined;
            }

            return allPages.length;
        },
    });
}

export function useNote(id: string | undefined) {
    return useQuery({
        queryKey: queryKeys.note(id),
        queryFn: () => fetchNote(id!),
        enabled: !!id,
    });
}

export function useUpdateNote() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<Note> }) =>
            updateNote(id, updates),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.note(variables.id) });
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });
}

export function useDeleteNote() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteNote(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });
}

export function useUser(authUserId?: string) {
    return useQuery({
        queryKey: queryKeys.user(authUserId),
        queryFn: getCurrentUser,
        enabled: !!authUserId,
        staleTime: 1000 * 60 * 5,
    });
}

export function usePreferences(authUserId?: string) {
    return useQuery({
        queryKey: queryKeys.preferences(authUserId),
        queryFn: fetchPreferences,
        enabled: !!authUserId,
        staleTime: 1000 * 60 * 5,
    });
}

export function useUpdatePreferences(authUserId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updatePreferences,
        onSuccess: (preferences) => {
            queryClient.setQueryData(queryKeys.preferences(preferences.user_id), preferences);
            if (authUserId && authUserId !== preferences.user_id) {
                queryClient.removeQueries({ queryKey: queryKeys.preferences(authUserId), exact: true });
            }
            queryClient.invalidateQueries({ queryKey: ['preferences'] });
        },
    });
}
